import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { MatrixGrid } from "../components/MatrixGrid";
import { ShapeTile } from "../components/ShapeTile";
import { Clock, Flag, ChevronLeft, ChevronRight, Check, AlertTriangle, RotateCw } from "lucide-react";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export const TestRunner = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [timeLimit, setTimeLimit] = useState(1500);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showNav, setShowNav] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [quizToken, setQuizToken] = useState(null);

  const loadQuestions = useCallback(() => {
    setLoadError(null);
    api.get("/test/questions").then((res) => {
      if (!res.data.questions || res.data.questions.length === 0) {
        setLoadError("empty");
        return;
      }
      setQuestions(res.data.questions);
      setQuizToken(res.data.quiz_token);
      setTimeLimit(res.data.time_limit);
      setTimeLeft(res.data.time_limit);
    }).catch((e) => {
      const status = e?.response?.status;
      if (status === 401) {
        navigate("/");
      } else if (status === 429) {
        navigate("/instructions");
      } else {
        setLoadError("network");
      }
    });
  }, [navigate]);

  useEffect(() => { loadQuestions(); }, [loadQuestions]);

  const doSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const elapsed = timeLimit - (timeLeft ?? 0);
      const res = await api.post("/test/submit", { answers, time_taken: elapsed, origin: window.location.origin, quiz_token: quizToken });
      navigate(`/results/${res.data.attempt_id}`);
    } catch (e) {
      const status = e?.response?.status;
      setSubmitting(false);
      if (status === 429) {
        navigate("/instructions");
      } else if (status === 401) {
        navigate("/");
      } else {
        setLoadError("submit");
      }
    }
  }, [submitting, answers, timeLeft, timeLimit, navigate, quizToken]);

  useEffect(() => {
    if (timeLeft === null) return;
    if (timeLeft <= 0) {
      doSubmit();
      return;
    }
    const t = setTimeout(() => setTimeLeft((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, doSubmit]);

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#0B0F17]">
        <Header />
        <div className="flex flex-col items-center justify-center h-[70vh] gap-4 px-5 text-center" data-testid="test-load-error">
          <div className="w-14 h-14 rounded-2xl bg-rose-500/15 flex items-center justify-center">
            <AlertTriangle className="w-7 h-7 text-rose-400" />
          </div>
          <p className="text-lg font-semibold text-white">
            {loadError === "submit" ? "We couldn't save your answers" : "We couldn't load the assessment"}
          </p>
          <p className="text-sm text-slate-400 max-w-sm">
            {loadError === "empty"
              ? "No questions were returned. Please try again in a moment."
              : "This is usually a brief network hiccup. Please try again."}
          </p>
          <div className="flex gap-3">
            <button
              data-testid="retry-load-button"
              onClick={() => (loadError === "submit" ? doSubmit() : loadQuestions())}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-500 text-white font-semibold hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] transition-all"
            >
              <RotateCw className="w-4 h-4" /> Try again
            </button>
            <button
              onClick={() => navigate("/instructions")}
              className="px-6 py-3 rounded-xl border border-white/10 text-slate-300 hover:bg-white/5"
            >
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!questions.length || timeLeft === null) {
    return (
      <div className="min-h-screen bg-[#0B0F17]">
        <Header />
        <div className="flex flex-col items-center justify-center h-[70vh] gap-4 text-slate-400">
          <div className="w-10 h-10 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" />
          <span className="font-mono-data text-sm">Loading assessment…</span>
        </div>
      </div>
    );
  }

  const q = questions[current];
  const selected = answers[q.id];
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / questions.length) * 100;
  const lowTime = timeLeft <= 60;

  const pick = (idx) => setAnswers((a) => ({ ...a, [q.id]: idx }));
  const toggleFlag = () => setFlagged((f) => ({ ...f, [q.id]: !f[q.id] }));

  return (
    <div className="min-h-screen bg-[#0B0F17]">
      <Header />
      {/* timer + progress bar */}
      <div className="sticky top-16 z-40 backdrop-blur-xl bg-[#0B0F17]/90 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-5 py-3 flex items-center gap-4">
          <div
            data-testid="test-timer"
            className={`flex items-center gap-2 font-mono-data font-bold text-lg ${lowTime ? "timer-warn" : "text-cyan-400"}`}
          >
            <Clock className="w-5 h-5" /> {fmt(timeLeft)}
          </div>
          <div className="flex-1">
            <div data-testid="progress-bar" className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cyan-500 to-indigo-500 transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <div className="text-sm text-slate-400 font-mono-data whitespace-nowrap">{answeredCount}/{questions.length}</div>
          <button
            data-testid="toggle-navigator-button"
            onClick={() => setShowNav((s) => !s)}
            className="text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10"
          >
            Navigator
          </button>
        </div>
      </div>

      {showNav && (
        <div className="max-w-4xl mx-auto px-5 pt-4">
          <div className="grid grid-cols-8 sm:grid-cols-10 gap-2 p-4 rounded-2xl bg-[#131B2A] border border-white/10">
            {questions.map((qq, i) => {
              const done = answers[qq.id] !== undefined;
              const fl = flagged[qq.id];
              return (
                <button
                  key={qq.id}
                  data-testid={`nav-dot-${i}`}
                  onClick={() => { setCurrent(i); setShowNav(false); }}
                  className={`h-9 rounded-lg text-xs font-semibold border transition-colors relative ${
                    i === current ? "border-cyan-400 text-cyan-300 bg-cyan-500/10" :
                    done ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" :
                    "border-white/10 text-slate-400 hover:bg-white/5"
                  }`}
                >
                  {i + 1}
                  {fl && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* question */}
      <div className="max-w-4xl mx-auto px-5 py-8">
        <div key={q.id} className="fade-up">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <span className="font-sub font-bold text-2xl text-white">Q{current + 1}</span>
              <span className="text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-indigo-500/15 text-indigo-300 border border-indigo-500/20">
                {q.category}
              </span>
            </div>
            <button
              data-testid="flag-question-button"
              onClick={toggleFlag}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                flagged[q.id] ? "border-amber-400/50 text-amber-300 bg-amber-500/10" : "border-white/10 text-slate-400 hover:bg-white/5"
              }`}
            >
              <Flag className="w-3.5 h-3.5" /> {flagged[q.id] ? "Flagged" : "Flag"}
            </button>
          </div>

          <h2 className="text-lg sm:text-xl font-semibold text-slate-100 leading-relaxed mb-7">{q.prompt}</h2>

          {q.type === "matrix" && (
            <div className="flex justify-center mb-9">
              <MatrixGrid grid={q.grid} />
            </div>
          )}

          <div className={q.type === "matrix" ? "grid grid-cols-2 sm:grid-cols-4 gap-3" : "grid gap-3"}>
            {q.options.map((opt, idx) => {
              const isSel = selected === idx;
              return (
                <button
                  key={`${q.id}-opt-${idx}`}
                  data-testid={`option-button-${idx}`}
                  onClick={() => pick(idx)}
                  className={`group relative flex items-center gap-3 rounded-xl border transition-all duration-200 ${
                    q.type === "matrix" ? "justify-center p-4 aspect-square sm:aspect-auto sm:h-32" : "px-5 py-4 text-left"
                  } ${
                    isSel ? "border-cyan-400 bg-cyan-500/15 shadow-[0_0_25px_rgba(6,182,212,0.2)]" : "border-white/10 bg-[#182234] hover:bg-[#202E46] hover:border-cyan-500/40"
                  }`}
                >
                  <span className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center text-xs font-bold ${
                    isSel ? "border-cyan-400 bg-cyan-400 text-slate-900" : "border-white/20 text-slate-400"
                  }`}>
                    {isSel ? <Check className="w-3.5 h-3.5" /> : String.fromCharCode(65 + idx)}
                  </span>
                  {q.type === "matrix" ? (
                    <ShapeTile tile={opt} size={64} />
                  ) : (
                    <span className="text-slate-200">{opt}</span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between mt-10">
            <button
              data-testid="prev-question-button"
              onClick={() => setCurrent((c) => Math.max(0, c - 1))}
              disabled={current === 0}
              className="inline-flex items-center gap-1.5 px-5 py-3 rounded-xl border border-white/10 text-slate-300 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>

            {current < questions.length - 1 ? (
              <button
                data-testid="next-question-button"
                onClick={() => setCurrent((c) => Math.min(questions.length - 1, c + 1))}
                className="inline-flex items-center gap-1.5 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-500 text-white font-semibold hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] transition-all"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                data-testid="submit-test-button"
                onClick={doSubmit}
                disabled={submitting}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-amber-600 text-slate-900 font-bold hover:shadow-[0_0_30px_rgba(245,158,11,0.4)] transition-all disabled:opacity-60"
              >
                {submitting ? "Scoring…" : "Finish & see results"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
